import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

export class TabsComponent extends Component {
  requiredRefs = ['tab', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  init() {
    // 默认选中第一个
    const tabs = /** @type {HTMLElement[]} */ (this.refs.tab);
    
    if (tabs && tabs.length > 0) {
      // 检查是否有已经激活的 tab, 如果没有则激活第一个
      const activeTab = tabs.find(tab => tab.getAttribute('aria-selected') === 'true');
      if (!activeTab) {
        this.activateTab(0);
      }
    }
  }

  /**
   * 切换 Tab
   * HTML绑定: on:click="select"
   * @param {Event} event
   */
  select(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const tabs = /** @type {HTMLElement[]} */ (this.refs.tab);
    
    if (!tabs) return;

    // 找到点击的 tab 的索引
    const index = tabs.indexOf(target);
    
    if (index !== -1) {
      this.activateTab(index);
    }
  }

  /**
   * 激活指定索引的 Tab
   * @param {number} index
   */
  activateTab(index) {
    const tabs = /** @type {HTMLElement[]} */ (this.refs.tab);
    const panels = /** @type {HTMLElement[]} */ (this.refs.panel);

    if (!tabs || !panels) return;

    tabs.forEach((tab, i) => {
      const isSelected = i === index;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.classList.toggle('tabs__tab--active', isSelected);
      
      // 处理 panel
      if (panels[i]) {
        panels[i].classList.toggle('tabs__panel--active', isSelected);
      }
    });
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}

export class SpxTabGroup extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.init();
    this.updateNavButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  updatedCallback() {
    super.updatedCallback();
    this.init();
    this.updateNavButtons();
  }

  getTabs() {
    return Array.from(this.querySelectorAll('spx-tab[slot="nav"]')).filter((el) => el instanceof HTMLElement);
  }

  getPanels() {
    return Array.from(this.querySelectorAll('spx-panel, spx-tab-panel')).filter((el) => el instanceof HTMLElement);
  }

  init() {
    const tabs = this.getTabs();
    const panels = this.getPanels();

    if (!tabs.length || !panels.length) return;

    const useAttrMapping = tabs.every((tab) => tab.hasAttribute('panel')) && panels.every((panel) => panel.hasAttribute('name'));

    const activeTabFromMarkup = tabs.find(
      (tab) => (tab.getAttribute('aria-selected') === 'true' || tab.hasAttribute('active')) && !tab.hasAttribute('disabled')
    );

    const activePanelFromAttr = this.getAttribute('value');
    const activeTabFromAttr = (() => {
      if (!activePanelFromAttr) return null;

      if (useAttrMapping) {
        return tabs.find((tab) => tab.getAttribute('panel') === activePanelFromAttr && !tab.hasAttribute('disabled')) || null;
      }

      const index = Number.parseInt(activePanelFromAttr, 10);
      if (Number.isNaN(index)) return null;
      const tab = tabs[index];
      if (!tab) return null;
      if (tab.hasAttribute('disabled')) return null;
      return tab;
    })();

    const firstEnabled = tabs.find((tab) => !tab.hasAttribute('disabled')) || null;
    const tabToActivate = activeTabFromAttr || activeTabFromMarkup || firstEnabled;

    if (!tabToActivate) return;

    if (useAttrMapping) {
      const panel = tabToActivate.getAttribute('panel') || '';
      if (!panel) return;
      this.activatePanel(panel);
      this.updateNavButtons();
      return;
    }

    const index = tabs.indexOf(tabToActivate);
    if (index === -1) return;
    this.activateIndex(index);
    this.updateNavButtons();
  }

  getEnabledTabs() {
    return this.getTabs().filter((tab) => !tab.hasAttribute('disabled'));
  }

  getActiveTab() {
    return this.getTabs().find((tab) => tab.getAttribute('aria-selected') === 'true') || null;
  }

  /**
   * 查找第一个可横向滚动的祖先（不滚动 document / 整页）。
   * @param {HTMLElement} el
   */
  #findHorizontalScrollParent(el) {
    let parent = el.parentElement;
    while (parent instanceof HTMLElement) {
      const style = getComputedStyle(parent);
      const ox = style.overflowX;
      if (
        (ox === 'auto' || ox === 'scroll' || ox === 'overlay') &&
        parent.scrollWidth > parent.clientWidth + 1
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  /**
   * 仅在横向滚动容器内对齐 tab，避免 scrollIntoView 连锁滚动视口。
   * @param {HTMLElement} tab
   * @param {HTMLElement} scroller
   * @param {ScrollBehavior} behavior
   */
  #scrollTabIntoHorizontalScroller(tab, scroller, behavior) {
    const tabRect = tab.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const delta = tabRect.left - scrollerRect.left;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const nextLeft = Math.max(0, Math.min(maxScroll, scroller.scrollLeft + delta));
    scroller.scrollTo({ left: nextLeft, behavior });
  }

  /**
   * 将当前选中的 tab 滚进横向导航可视区，并尽量靠左对齐（SimpleBar 下用手动 scrollLeft，更可靠）。
   * 不使用 scrollIntoView，防止整页被带动滚动。
   */
  #scrollActiveTabIntoNav() {
    const tab = this.getActiveTab();
    if (!(tab instanceof HTMLElement)) return;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    const run = (simplebarRetry = 0) => {
      const scroller = tab.closest('.simplebar-content-wrapper');

      if (scroller instanceof HTMLElement) {
        this.#scrollTabIntoHorizontalScroller(tab, scroller, behavior);
        return;
      }

      if (tab.closest('[data-simplebar]') && simplebarRetry < 5) {
        requestAnimationFrame(() => run(simplebarRetry + 1));
        return;
      }

      const hScroll = this.#findHorizontalScrollParent(tab);
      if (hScroll) {
        this.#scrollTabIntoHorizontalScroller(tab, hScroll, behavior);
      }
    };

    requestAnimationFrame(() => run(0));
  }

  activateRelative(delta = 0) {
    const tabs = this.getTabs();
    const panels = this.getPanels();
    const useAttrMapping = tabs.every((t) => t.hasAttribute('panel')) && panels.every((p) => p.hasAttribute('name'));

    const enabledTabs = tabs.filter((tab) => !tab.hasAttribute('disabled'));
    if (!enabledTabs.length) return;

    const active = this.getActiveTab() || enabledTabs[0] || null;
    if (!active) return;

    const enabledIndex = enabledTabs.indexOf(active);
    if (enabledIndex === -1) return;

    const nextEnabledIndex = enabledIndex + delta;
    const nextTab = enabledTabs[nextEnabledIndex];
    if (!nextTab) {
      this.updateNavButtons();
      return;
    }

    if (useAttrMapping) {
      const panel = nextTab.getAttribute('panel') || '';
      if (!panel) return;
      this.activatePanel(panel);
    } else {
      const allIndex = tabs.indexOf(nextTab);
      if (allIndex === -1) return;
      this.activateIndex(allIndex);
    }

    this.updateNavButtons();
    nextTab.focus({ preventScroll: true });
  }

  updateNavButtons() {
    const enabledTabs = this.getEnabledTabs();
    if (!enabledTabs.length) return;

    const active = this.getActiveTab() || enabledTabs[0] || null;
    if (!active) return;

    const enabledIndex = enabledTabs.indexOf(active);
    const isPrevDisabled = enabledIndex <= 0;
    const isNextDisabled = enabledIndex >= enabledTabs.length - 1;

    const prevButtons = Array.from(this.querySelectorAll('.spx-button--prev'));
    const nextButtons = Array.from(this.querySelectorAll('.spx-button--next'));

    prevButtons.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      btn.classList.toggle('swiper-button-disabled', isPrevDisabled);
      btn.toggleAttribute('aria-disabled', isPrevDisabled);
    });

    nextButtons.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      btn.classList.toggle('swiper-button-disabled', isNextDisabled);
      btn.toggleAttribute('aria-disabled', isNextDisabled);
    });
  }

  activatePanel(panelName = '') {
    const tabs = this.getTabs();
    const panels = this.getPanels();

    tabs.forEach((tab) => {
      const isDisabled = tab.hasAttribute('disabled');
      const isSelected = !isDisabled && tab.getAttribute('panel') === panelName;

      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.toggleAttribute('data-spx-tab-active', isSelected);
      tab.toggleAttribute('active', isSelected);
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      tab.toggleAttribute('aria-disabled', isDisabled);
    });

    panels.forEach((panel) => {
      const name = panel.getAttribute('name') || '';
      const isActive = name === panelName;

      panel.setAttribute('role', 'tabpanel');
      panel.toggleAttribute('data-spx-tab-active', isActive);
    });

    if (panelName) this.setAttribute('value', panelName);

    this.#scrollActiveTabIntoNav();
  }

  activateIndex(index = 0) {
    const tabs = this.getTabs();
    const panels = this.getPanels();

    if (!tabs.length || !panels.length) return;

    const activeTab = tabs[index];
    if (!activeTab) return;
    if (activeTab.hasAttribute('disabled')) return;

    tabs.forEach((tab, i) => {
      const isDisabled = tab.hasAttribute('disabled');
      const isSelected = !isDisabled && i === index;

      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.toggleAttribute('data-spx-tab-active', isSelected);
      tab.toggleAttribute('active', isSelected);
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      tab.toggleAttribute('aria-disabled', isDisabled);
    });

    panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.setAttribute('role', 'tabpanel');
      panel.toggleAttribute('data-spx-tab-active', isActive);
    });

    this.setAttribute('value', String(index));

    this.#scrollActiveTabIntoNav();
  }

  focusActiveTab() {
    const tabs = this.getTabs();
    const activeTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (activeTab) activeTab.focus({ preventScroll: true });
  }

  #onClick = (event = new MouseEvent('click')) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const navBtn = target.closest('.spx-button--prev, .spx-button--next');
    if (navBtn instanceof HTMLElement && this.contains(navBtn)) {
      if (navBtn.classList.contains('swiper-button-disabled')) return;
      if (navBtn.classList.contains('spx-button--prev')) this.activateRelative(-1);
      if (navBtn.classList.contains('spx-button--next')) this.activateRelative(1);
      return;
    }

    const tab = target.closest('spx-tab[slot="nav"]');
    if (!(tab instanceof HTMLElement)) return;
    if (!this.contains(tab)) return;
    if (tab.hasAttribute('disabled')) return;

    const tabs = this.getTabs();
    const panels = this.getPanels();
    const useAttrMapping = tabs.every((t) => t.hasAttribute('panel')) && panels.every((p) => p.hasAttribute('name'));

    if (useAttrMapping) {
      const panel = tab.getAttribute('panel') || '';
      if (!panel) return;
      this.activatePanel(panel);
    } else {
      const index = tabs.indexOf(tab);
      if (index === -1) return;
      this.activateIndex(index);
    }
    this.updateNavButtons();
    tab.focus({ preventScroll: true });
  };

  #onKeyDown = (event = new KeyboardEvent('keydown')) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const currentTab = target.closest('spx-tab[slot="nav"]');
    if (!(currentTab instanceof HTMLElement)) return;
    if (!this.contains(currentTab)) return;

    const allTabs = this.getTabs();
    const panels = this.getPanels();
    const useAttrMapping = allTabs.every((t) => t.hasAttribute('panel')) && panels.every((p) => p.hasAttribute('name'));

    const enabledTabs = allTabs.filter((tab) => !tab.hasAttribute('disabled'));
    if (!enabledTabs.length) return;

    const tabs = enabledTabs;

    const index = enabledTabs.indexOf(currentTab);
    if (index === -1) return;

    const key = event.key;

    if (key === 'ArrowRight' || key === 'ArrowLeft' || key === 'Home' || key === 'End') {
      event.preventDefault();

      let nextIndex = index;

      if (key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      if (key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (key === 'Home') nextIndex = 0;
      if (key === 'End') nextIndex = tabs.length - 1;

      const nextTab = tabs[nextIndex];
      if (!nextTab) return;

      if (useAttrMapping) {
        const panel = nextTab.getAttribute('panel') || '';
        if (!panel) return;
        this.activatePanel(panel);
      } else {
        const allIndex = allTabs.indexOf(nextTab);
        if (allIndex === -1) return;
        this.activateIndex(allIndex);
      }
      this.updateNavButtons();
      nextTab.focus({ preventScroll: true });
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();

      if (useAttrMapping) {
        const panel = currentTab.getAttribute('panel') || '';
        if (!panel) return;
        this.activatePanel(panel);
      } else {
        const allIndex = allTabs.indexOf(currentTab);
        if (allIndex === -1) return;
        this.activateIndex(allIndex);
      }
      this.updateNavButtons();
    }
  };
}

export class SpxTab extends HTMLElement {}

export class SpxPanel extends HTMLElement {}

export class SpxTabPanel extends HTMLElement {}

if (!customElements.get('spx-tab-group')) {
  customElements.define('spx-tab-group', SpxTabGroup);
}

if (!customElements.get('spx-tab')) {
  customElements.define('spx-tab', SpxTab);
}

if (!customElements.get('spx-panel')) {
  customElements.define('spx-panel', SpxPanel);
}

if (!customElements.get('spx-tab-panel')) {
  customElements.define('spx-tab-panel', SpxTabPanel);
}
